import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { fetchExecutiveOverview } from '../../services/executiveGovernanceApi'
import { fetchRuntimeTimeline } from '../../services/complianceRuntimeApi'
import { featureFlags } from '../../config/featureFlags'
import { pressureBand, pressureColor, normalizeStability, stabilityColor, formatRelativeTime } from '../../utils/operationalUx'
import { Button } from '../ui/Button'
import styles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'

type Props = {
  overviewStandard: string
}

/** Compliance Center Overview — executive posture + runtime timeline (inside enterprise section). */
export default function EnterpriseOperationalOverviewPanel({ overviewStandard }: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [posture, setPosture] = useState<Awaited<ReturnType<typeof fetchExecutiveOverview>> | null>(null)
  const [timeline, setTimeline] = useState<Array<{ id: string; at: string; eventType: string; severity: string; summary: string }>>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const tasks: Promise<void>[] = []
      if (featureFlags.EXECUTIVE_GOVERNANCE_V1) {
        tasks.push(
          fetchExecutiveOverview().then((p) => {
            setPosture(p)
          }),
        )
      }
      if (featureFlags.COMPLIANCE_RUNTIME_V1) {
        const std = overviewStandard !== 'ALL' ? overviewStandard : undefined
        tasks.push(
          fetchRuntimeTimeline(std).then((r) => {
            setTimeline((r.timeline || []).slice(0, 5))
          }),
        )
      }
      await Promise.all(tasks)
    } catch {
      setPosture(null)
      setTimeline([])
    } finally {
      setLoading(false)
    }
  }, [overviewStandard])

  useEffect(() => {
    void load()
  }, [load])

  const stability = normalizeStability(posture?.certificationRiskLevel)
  const pressure = pressureBand(posture?.overallOperationalPressure)
  const story = posture?.explanations?.lines?.[0]?.message

  return (
    <>
      {loading ? <p className={styles.emptyState}>{t('common.loading', 'Loading…')}</p> : null}
      {!loading && !posture && timeline.length === 0 ? (
        <p className={styles.emptyState}>
          {t('enterpriseOperationalUx.runOperationalRecompute', 'Run operational recompute to establish executive and runtime baseline.')}
        </p>
      ) : null}
      {posture ? (
        <div className={styles.postureGrid}>
          <div className={styles.postureMetric}>
            <p className={styles.postureMetricLabel}>{t('executiveGovernance.certificationStability', 'Certification Stability')}</p>
            <p className={styles.postureMetricValue} style={{ color: stabilityColor(stability) }}>
              {stability}
            </p>
          </div>
          <div className={styles.postureMetric}>
            <p className={styles.postureMetricLabel}>{t('executiveGovernance.operationalPressure', 'Operational Pressure')}</p>
            <p className={styles.postureMetricValue} style={{ color: pressureColor(pressure) }}>
              {pressure}
            </p>
          </div>
          <div className={styles.postureMetric}>
            <p className={styles.postureMetricLabel}>{t('executiveGovernance.degradedControls', 'degraded controls')}</p>
            <p className={styles.postureMetricValue}>{posture.degradedControlCount ?? 0}</p>
          </div>
          <div className={styles.postureMetric}>
            <p className={styles.postureMetricLabel}>{t('enterpriseOperationalUx.staleEvidence', 'Stale evidence')}</p>
            <p className={styles.postureMetricValue}>{posture.staleEvidenceCount ?? 0}</p>
          </div>
        </div>
      ) : null}
      {story ? <p className={styles.postureStory}>{story}</p> : null}
      {timeline.length > 0 ? (
        <ul className={styles.timelineList}>
          {timeline.map((item) => (
            <li key={item.id} className={styles.timelineItem}>
              <span className={styles.timelineTime}>{formatRelativeTime(item.at)}</span>
              <span className={styles.timelineEvent}>{item.eventType}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <div className={styles.workspaceActions}>
        <Button type="button" size="small" color="outline" onClick={() => navigate('/compliance-center/executive-governance')}>
          {t('executiveGovernance.executiveGovernance', 'Executive Governance')}
        </Button>
        {featureFlags.COMPLIANCE_RUNTIME_V1 ? (
          <Button type="button" size="small" color="outline" onClick={() => navigate('/compliance-center/operational-events')}>
            {t('enterpriseOperationalUx.openRuntimeEvents', 'Runtime events')}
          </Button>
        ) : null}
      </div>
    </>
  )
}
